import {
    Autocomplete,
    Badge,
    Banner,
    Button,
    Card,
    IndexTable,
    Page,
    Pagination,
    Spinner,
    Text,
    TextField,
    Thumbnail,
} from '@shopify/polaris';
import { DeleteIcon, ImageIcon, SearchIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import agent from '../../app/api/agent';
import { SafeAppBridgeModal as Modal, SafeAppBridgeTitleBar as TitleBar } from '../../app/components/SafeAppBridge';
import type {
    CollectionMappedVehicle,
    CollectionMappingDetails,
    SaveCollectionMappingPayload,
} from '../../app/models/CollectionMapping';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import { useStore } from '../../app/stores/store';
import { getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldValues,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    hasRequiredFitmentFieldValues,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';

const PAGE_SIZE = 50;
type SelectOption = { label: string; value: string };
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];
type MappedVehicleView = 'grouped' | 'individual';
type MappedVehicleRow = {
    key: string;
    vehicles: CollectionMappedVehicle[];
    fieldValues: Record<string, string>;
};

const isStandardFitmentField = (field: FitmentField, key: string) =>
    normalizeFitmentKey(field) === key;

const orderFitmentFieldsForEditor = (fields: FitmentField[], yearField?: FitmentField) => {
    if (!yearField) return fields;

    const makeField = fields.find((field) => isStandardFitmentField(field, 'make'));
    const modelField = fields.find((field) => isStandardFitmentField(field, 'model'));
    const prioritized = [makeField, modelField].filter((field): field is FitmentField => !!field);
    const prioritizedSet = new Set(prioritized);
    return [
        ...prioritized,
        ...fields.filter((field) => field !== yearField && !prioritizedSet.has(field)),
        yearField,
    ];
};

const getYearRangeValues = (startValue: string, endValue: string) => {
    const start = startValue.trim();
    const end = endValue.trim();
    if (!start) {
        return end
            ? { values: [] as string[], error: 'Choose a starting year before choosing Year To.' }
            : { values: [''], error: '' };
    }

    if (!/^\d{4}$/.test(start) || Number(start) < 1900 || Number(start) > 2099) {
        return { values: [] as string[], error: 'Vehicle years must be between 1900 and 2099.' };
    }

    if (!end || end === start) return { values: [start], error: '' };
    if (!/^\d{4}$/.test(end) || Number(end) < 1900 || Number(end) > 2099) {
        return { values: [] as string[], error: 'Vehicle years must be between 1900 and 2099.' };
    }

    const startYear = Number(start);
    const endYear = Number(end);
    if (startYear > endYear) {
        return { values: [] as string[], error: 'Year To must be the same as or after the starting year.' };
    }

    return {
        values: Array.from({ length: endYear - startYear + 1 }, (_, index) => `${startYear + index}`),
        error: '',
    };
};

const getAutocompleteOptions = (options: SelectOption[], value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    return options
        .filter((option) => option.value)
        .filter((option) => !normalizedValue
            || option.label.toLowerCase().includes(normalizedValue)
            || option.value.toLowerCase().includes(normalizedValue));
};

const FitmentValueAutocomplete = ({
    label,
    value,
    options,
    disabled,
    loading,
    error,
    placeholder,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    placeholder?: string;
    onChange: (value: string) => void;
}) => {
    const textField = (
        <Autocomplete.TextField
            label={label}
            value={value}
            onChange={onChange}
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            clearButton={!!value}
            onClearButtonClick={() => onChange('')}
            error={error ? true : undefined}
        />
    );

    return (
        <Autocomplete
            options={getAutocompleteOptions(options, value)}
            selected={value ? [value] : []}
            textField={textField}
            onSelect={(selected) => onChange(selected[0] ?? '')}
            loading={loading}
            emptyState={<div style={{ padding: 12 }}>No saved values. Type a new value to use it.</div>}
        />
    );
};

const formatYearRange = (values: string[]) => {
    const years = Array.from(new Set(values.filter((value) => /^\d+$/.test(value)).map(Number)))
        .sort((left, right) => left - right);
    if (years.length === 0) return Array.from(new Set(values.filter(Boolean))).join(', ');

    const ranges: string[] = [];
    let start = years[0];
    let previous = years[0];
    years.slice(1).forEach((year) => {
        if (year === previous + 1) {
            previous = year;
            return;
        }
        ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
        start = year;
        previous = year;
    });
    ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
    return ranges.join(', ');
};

const buildIndividualRows = (vehicles: CollectionMappedVehicle[], fields: FitmentField[]): MappedVehicleRow[] =>
    vehicles.map((vehicle) => ({
        key: String(vehicle.mappingId),
        vehicles: [vehicle],
        fieldValues: fields.reduce<Record<string, string>>((values, field) => {
            values[normalizeFitmentKey(field)] = vehicle.fieldValues[normalizeFitmentKey(field)] || '-';
            return values;
        }, {}),
    }));

const buildGroupedRows = (
    vehicles: CollectionMappedVehicle[],
    fields: FitmentField[],
    yearField: FitmentField,
): MappedVehicleRow[] => {
    const nonYearFields = fields.filter((field) => field !== yearField);
    const groups = new Map<string, CollectionMappedVehicle[]>();
    vehicles.forEach((vehicle) => {
        const key = nonYearFields
            .map((field) => `${normalizeFitmentKey(field)}=${vehicle.fieldValues[normalizeFitmentKey(field)] || ''}`)
            .join('\u001e');
        groups.set(key, [...(groups.get(key) ?? []), vehicle]);
    });

    return Array.from(groups.entries()).map(([key, group]) => ({
        key: `group:${key}`,
        vehicles: group,
        fieldValues: fields.reduce<Record<string, string>>((values, field) => {
            const fieldKey = normalizeFitmentKey(field);
            values[fieldKey] = field === yearField
                ? formatYearRange(group.map((vehicle) => vehicle.fieldValues[fieldKey] || ''))
                : group[0].fieldValues[fieldKey] || '-';
            return values;
        }, {}),
    }));
};

const showToast = (message: string) => window.shopify?.toast?.show?.(message);

const formatMappedDate = (value: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? '-'
        : new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
};

const CollectionDetailsPage: React.FC = () => {
    const { id } = useParams();
    const collectionId = Number(id);
    const navigate = useNavigate();
    const location = useLocation();
    const { userStore } = useStore();
    const [details, setDetails] = useState<CollectionMappingDetails | null>(null);
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [removeCandidates, setRemoveCandidates] = useState<CollectionMappedVehicle[]>([]);
    const [removing, setRemoving] = useState(false);
    const [mappedVehicleView, setMappedVehicleView] = useState<MappedVehicleView>('grouped');
    const [mappedVehicleSearchVisible, setMappedVehicleSearchVisible] = useState(false);
    const [selectedFitmentValues, setSelectedFitmentValues] = useState<FitmentFieldValues>({});
    const [selectedFitmentYearTo, setSelectedFitmentYearTo] = useState('');
    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [reassignment, setReassignment] = useState<{
        payload: SaveCollectionMappingPayload;
        count: number;
        names: string[];
    } | null>(null);
    const latestRequestId = useRef(0);

    const visibleFields = useMemo(() => getVisibleFitmentFields(fitmentCatalog), [fitmentCatalog]);
    const standardYearField = useMemo(
        () => visibleFields.find((field) => isStandardFitmentField(field, 'year')),
        [visibleFields],
    );
    const editorFields = useMemo(
        () => orderFitmentFieldsForEditor(visibleFields, standardYearField),
        [standardYearField, visibleFields],
    );
    const selectedYearValue = standardYearField
        ? getFitmentFieldValue(selectedFitmentValues, standardYearField)
        : '';
    const selectedYearRange = useMemo(
        () => standardYearField
            ? getYearRangeValues(selectedYearValue, selectedFitmentYearTo)
            : { values: [''], error: '' },
        [selectedFitmentYearTo, selectedYearValue, standardYearField],
    );
    const hasVehicleContext = editorFields.length > 0
        && (editorFields.some((field) => field.isRequired)
            ? hasRequiredFitmentFieldValues(editorFields, selectedFitmentValues)
            : hasAnyFitmentFieldValue(editorFields, selectedFitmentValues));
    const canAddMapping = hasVehicleContext && !selectedYearRange.error;
    const effectiveMappedVehicleView: MappedVehicleView = standardYearField
        ? mappedVehicleView
        : 'individual';
    const mappedVehicleRows = useMemo(() => {
        const vehicles = details?.vehicles ?? [];
        return effectiveMappedVehicleView === 'grouped' && standardYearField
            ? buildGroupedRows(vehicles, editorFields, standardYearField)
            : buildIndividualRows(vehicles, editorFields);
    }, [details?.vehicles, editorFields, effectiveMappedVehicleView, standardYearField]);
    const backPath = typeof location.state?.from === 'string' && location.state.from.startsWith('/products')
        ? location.state.from
        : '/products?view=collections';
    const shopHandle = getShopifyStoreHandle(userStore.user?.shopDomain);
    const shopifyAdminUrl = shopHandle && Number.isFinite(collectionId)
        ? `https://admin.shopify.com/store/${shopHandle}/collections/${collectionId}`
        : '';

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setPage(1);
            setSearchTerm(query.trim());
        }, 350);
        return () => window.clearTimeout(timeout);
    }, [query]);

    useEffect(() => {
        let cancelled = false;
        agent.Settings.fitmentCatalog()
            .then((catalog) => {
                if (!cancelled) setFitmentCatalog(catalog);
            })
            .catch(() => {
                if (!cancelled) setFitmentCatalog(null);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setSelectedFitmentValues((current) => normalizeFitmentFieldValues(editorFields, current));
        if (!standardYearField) setSelectedFitmentYearTo('');
    }, [editorFields, standardYearField]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (editorFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                const normalizedValues = normalizeFitmentFieldValues(editorFields, selectedFitmentValues);
                const result = await agent.Vehicles.filterOptions(
                    normalizedValues.year || '',
                    normalizedValues.make || '',
                    '',
                    normalizedValues,
                    editorFields.map(normalizeFitmentKey),
                );
                if (cancelled) return;

                setFieldOptions(editorFields.reduce<Record<string, SelectOption[]>>((options, field) => {
                    const key = normalizeFitmentKey(field);
                    options[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'select'), value: '' },
                        ...(result.optionsByField?.[key] ?? []).map((value) => ({ label: value, value })),
                    ];
                    return options;
                }, {}));
            } catch {
                if (!cancelled) setFieldOptions({});
            } finally {
                if (!cancelled) setLoadingFieldOptions(false);
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [editorFields, selectedFitmentValues]);

    const loadDetails = useCallback(async () => {
        if (!collectionId || Number.isNaN(collectionId)) {
            setError('Invalid collection id.');
            setLoading(false);
            return;
        }

        const requestId = ++latestRequestId.current;
        setLoading(true);
        setError('');
        try {
            const result = await agent.CollectionMappings.details(collectionId, page, searchTerm, PAGE_SIZE);
            if (requestId === latestRequestId.current) setDetails(result);
        } catch (exception: any) {
            if (requestId === latestRequestId.current) {
                setError(exception?.message ?? 'Failed to load collection details.');
            }
        } finally {
            if (requestId === latestRequestId.current) setLoading(false);
        }
    }, [collectionId, page, searchTerm]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    const addMappings = useCallback(async (payload: SaveCollectionMappingPayload) => {
        if (!details) return;

        setAssigning(true);
        try {
            const result = await agent.CollectionMappings.saveMapping(payload);
            if (result.requiresReassignmentConfirmation) {
                setReassignment({
                    payload,
                    count: result.conflictingVehicleCount ?? 0,
                    names: result.conflictingVehicleNames ?? [],
                });
                return;
            }

            showToast(`${result.selectedVehicleCount} vehicle mapping${result.selectedVehicleCount === 1 ? '' : 's'} saved`);
            setSelectedFitmentValues({});
            setSelectedFitmentYearTo('');
            setReassignment(null);
            setQuery('');
            setSearchTerm('');
            setPage(1);
            await loadDetails();
        } catch (exception: any) {
            showToast(exception?.message ?? 'Failed to add collection mappings');
        } finally {
            setAssigning(false);
        }
    }, [details, loadDetails]);

    const addSelectedVehicle = useCallback(async () => {
        if (!details || !canAddMapping) return;

        const normalizedValues = normalizeFitmentFieldValues(editorFields, selectedFitmentValues);
        const legacyValues = getLegacyFieldValues(normalizedValues);
        await addMappings({
            collectionId: details.id,
            year: legacyValues.year,
            yearTo: standardYearField ? selectedFitmentYearTo.trim() : '',
            make: legacyValues.make,
            model: legacyValues.model,
            submodel: legacyValues.submodel,
            fieldValues: normalizedValues,
            allowReassignment: false,
        });
    }, [addMappings, canAddMapping, details, editorFields, selectedFitmentValues, selectedFitmentYearTo, standardYearField]);

    const confirmReassignment = useCallback(async () => {
        if (!reassignment) return;
        await addMappings({ ...reassignment.payload, allowReassignment: true });
    }, [addMappings, reassignment]);

    const removeMapping = useCallback(async () => {
        if (removeCandidates.length === 0 || !details) return;

        setRemoving(true);
        try {
            await agent.CollectionMappings.removeMappedVehicles(
                details.id,
                removeCandidates.map((candidate) => candidate.mappingId),
            );
            showToast(`Removed ${removeCandidates.length} collection mapping${removeCandidates.length === 1 ? '' : 's'}`);
            setRemoveCandidates([]);

            if (removeCandidates.length >= details.vehicles.length && page > 1) {
                setPage((current) => Math.max(1, current - 1));
            } else {
                await loadDetails();
            }
        } catch (exception: any) {
            showToast(exception?.message ?? 'Failed to remove collection mapping');
        } finally {
            setRemoving(false);
        }
    }, [details, loadDetails, page, removeCandidates]);

    const headings = [
        ...(editorFields.length > 0
            ? editorFields.map((field) => ({ title: field.label }))
            : [{ title: 'Vehicle' }]),
        { title: 'Source' },
        { title: 'Mapped' },
        { title: '' },
    ];

    return (
        <>
            <Modal
                id="remove-collection-mapping"
                open={removeCandidates.length > 0}
                onHide={() => !removing && setRemoveCandidates([])}
            >
                <div style={{ padding: 16 }}>
                    <Banner title={`Remove ${removeCandidates.length === 1 ? 'collection mapping' : 'mapping group'}?`} tone="warning">
                        {removeCandidates.length === 1
                            ? `“${removeCandidates[0].displayName}” will no longer be mapped to “${details?.title ?? 'this collection'}.” Product mappings are not affected.`
                            : `${removeCandidates.length} vehicle years will no longer be mapped to “${details?.title ?? 'this collection'}.” Product mappings are not affected.`}
                    </Banner>
                </div>
                <TitleBar title={removeCandidates.length === 1 ? 'Remove collection mapping' : 'Remove mapping group'}>
                    <button onClick={() => setRemoveCandidates([])} disabled={removing}>Cancel</button>
                    <button variant="primary" onClick={removeMapping} disabled={removing}>
                        {removeCandidates.length === 1 ? 'Remove mapping' : 'Remove group'}
                    </button>
                </TitleBar>
            </Modal>

            <Modal
                id="confirm-collection-reassignment"
                open={!!reassignment}
                onHide={() => !assigning && setReassignment(null)}
            >
                <div style={{ padding: 16, display: 'grid', gap: 12 }}>
                    <Banner title="Reassign vehicles to this collection?" tone="warning">
                        {`${reassignment?.count ?? 0} selected vehicle${reassignment?.count === 1 ? '' : 's'} are already mapped to another collection. Continuing will move those mappings to “${details?.title ?? 'this collection'}.”`}
                    </Banner>
                    {(reassignment?.names?.length ?? 0) > 0 && (
                        <div>
                            <Text as="p" variant="bodySm" fontWeight="semibold">Affected vehicles</Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                                {reassignment!.names.slice(0, 6).join(', ')}
                                {reassignment!.names.length > 6 ? ` and ${reassignment!.names.length - 6} more` : ''}
                            </Text>
                        </div>
                    )}
                </div>
                <TitleBar title="Confirm vehicle reassignment">
                    <button onClick={() => setReassignment(null)} disabled={assigning}>Cancel</button>
                    <button variant="primary" onClick={confirmReassignment} disabled={assigning}>Reassign vehicles</button>
                </TitleBar>
            </Modal>

            <Page
                fullWidth
                title={details?.title || 'Collection details'}
                backAction={{ content: 'Collections', onAction: () => navigate(backPath) }}
            >
                {error && (
                    <div style={{ marginBottom: 12 }}>
                        <Banner title="Unable to load collection" tone="critical">{error}</Banner>
                    </div>
                )}

                {!details && loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                        <Spinner accessibilityLabel="Loading collection details" size="large" />
                    </div>
                ) : details ? (
                    <div style={{ display: 'grid', gap: 16 }}>
                        <Card>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                                    <Thumbnail source={details.imageUrl || ImageIcon} alt={details.title} size="medium" />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', minWidth: 0 }}>
                                        <Text as="p" variant="bodyMd">
                                            Handle: {details.handle ? `/${details.handle}` : '-'}
                                        </Text>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Text as="span" variant="bodyMd">Type:</Text>
                                            <Badge>{details.collectionType || 'Shopify collection'}</Badge>
                                        </div>
                                        <Text as="p" variant="bodyMd">Products: {details.productsCount.toLocaleString()}</Text>
                                        <Text as="p" variant="bodyMd">Mapped vehicles: {details.mappedVehicleCount.toLocaleString()}</Text>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    {shopifyAdminUrl && (
                                        <Button url={shopifyAdminUrl} target="_blank" variant="plain">
                                            Shopify admin
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <div style={{ display: 'grid', gap: 3 }}>
                                <Text as="h3" variant="headingMd">Vehicle mappings</Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Add vehicles to this collection and manage its existing mappings.
                                </Text>
                            </div>

                            <div style={{ marginTop: 16, padding: 12, border: '1px solid #e3e3e3', borderRadius: 8, background: '#fafafa' }}>
                                <Text as="h4" variant="headingSm">Add vehicle</Text>
                                <div className="pf-fitment-selector pf-fitment-selector--flush">
                                    {editorFields.map((field) => {
                                        const key = normalizeFitmentKey(field);
                                        const isYearField = field === standardYearField;
                                        const fieldDisabled = assigning
                                            || isFitmentFieldDisabled(editorFields, selectedFitmentValues, field, false);
                                        return (
                                            <React.Fragment key={key}>
                                                <div className="pf-fitment-selector__field">
                                                    <FitmentValueAutocomplete
                                                        label={field.label}
                                                        options={fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'select'), value: '' }]}
                                                        value={getFitmentFieldValue(selectedFitmentValues, field)}
                                                        disabled={fieldDisabled}
                                                        loading={loadingFieldOptions}
                                                        placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                                        onChange={(value) => {
                                                            setSelectedFitmentValues((current) => updateFitmentFieldValue(editorFields, current, field, value));
                                                            if (isYearField) setSelectedFitmentYearTo('');
                                                        }}
                                                    />
                                                </div>
                                                {isYearField && (
                                                    <div className="pf-fitment-selector__field">
                                                        <FitmentValueAutocomplete
                                                            label="Year To"
                                                            options={fieldOptions[key] ?? []}
                                                            value={selectedFitmentYearTo}
                                                            disabled={assigning || !selectedYearValue}
                                                            loading={loadingFieldOptions}
                                                            error={selectedYearRange.error}
                                                            placeholder="Select year to"
                                                            onChange={setSelectedFitmentYearTo}
                                                        />
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    <div className="pf-fitment-selector__action">
                                        <Button
                                            variant="primary"
                                            onClick={addSelectedVehicle}
                                            disabled={!canAddMapping || assigning || loading}
                                            loading={assigning}
                                        >
                                            Add mapping
                                        </Button>
                                    </div>
                                </div>
                                {selectedYearRange.error && (
                                    <Text as="p" variant="bodySm" tone="critical">{selectedYearRange.error}</Text>
                                )}
                            </div>

                            <div style={{ marginTop: 18 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <Text as="h4" variant="headingSm">Mapped vehicles</Text>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        {mappedVehicleSearchVisible && (
                                            <div style={{ width: 320, maxWidth: '100%' }}>
                                                <TextField
                                                    label="Search mapped vehicles"
                                                    labelHidden
                                                    value={query}
                                                    onChange={setQuery}
                                                    onClearButtonClick={() => setQuery('')}
                                                    clearButton
                                                    autoFocus
                                                    placeholder="Search mapped vehicles"
                                                    autoComplete="off"
                                                />
                                            </div>
                                        )}
                                        {details.mappedVehicleCount > 0 && (
                                            <Button
                                                icon={SearchIcon}
                                                variant="tertiary"
                                                pressed={mappedVehicleSearchVisible}
                                                accessibilityLabel="Search mapped vehicles"
                                                onClick={() => {
                                                    if (mappedVehicleSearchVisible) {
                                                        setQuery('');
                                                        setSearchTerm('');
                                                        setPage(1);
                                                        setMappedVehicleSearchVisible(false);
                                                        return;
                                                    }

                                                    setMappedVehicleSearchVisible(true);
                                                }}
                                            />
                                        )}
                                        {standardYearField && details.mappedVehicleCount > 0 && (
                                            <div className="pf-mapped-vehicle-view-switch" role="group" aria-label="Mapped vehicle view">
                                                {([
                                                    { value: 'grouped' as MappedVehicleView, label: 'Grouped' },
                                                    { value: 'individual' as MappedVehicleView, label: 'Individual years' },
                                                ]).map((option) => (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        aria-pressed={effectiveMappedVehicleView === option.value}
                                                        className={`pf-mapped-vehicle-view-switch__button${effectiveMappedVehicleView === option.value ? ' pf-mapped-vehicle-view-switch__button--selected' : ''}`}
                                                        onClick={() => setMappedVehicleView(option.value)}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ marginTop: 10 }}>
                                    {mappedVehicleRows.length > 0 ? (
                                        <IndexTable
                                            resourceName={{ singular: 'vehicle', plural: 'vehicles' }}
                                            itemCount={mappedVehicleRows.length}
                                            headings={headings as any}
                                            selectable={false}
                                            loading={loading}
                                        >
                                            {mappedVehicleRows.map((row, index) => {
                                                const firstVehicle = row.vehicles[0];
                                                const sources = Array.from(new Set(row.vehicles.map((vehicle) => vehicle.source || '-')));
                                                const latestMappedAt = row.vehicles
                                                    .map((vehicle) => vehicle.mappedAt)
                                                    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];
                                                return (
                                                    <IndexTable.Row id={row.key} key={row.key} position={index}>
                                                        {editorFields.length > 0 ? editorFields.map((field) => (
                                                            <IndexTable.Cell key={`${row.key}-${normalizeFitmentKey(field)}`}>
                                                                {row.fieldValues[normalizeFitmentKey(field)] || '-'}
                                                            </IndexTable.Cell>
                                                        )) : (
                                                            <IndexTable.Cell>{firstVehicle.displayName || '-'}</IndexTable.Cell>
                                                        )}
                                                        <IndexTable.Cell>{sources.length === 1 ? sources[0] : 'Mixed'}</IndexTable.Cell>
                                                        <IndexTable.Cell>{formatMappedDate(latestMappedAt)}</IndexTable.Cell>
                                                        <IndexTable.Cell>
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                <Button
                                                                    icon={DeleteIcon}
                                                                    variant="tertiary"
                                                                    tone="critical"
                                                                    onClick={() => setRemoveCandidates(row.vehicles)}
                                                                    disabled={removing}
                                                                >
                                                                    {effectiveMappedVehicleView === 'grouped' && row.vehicles.length > 1 ? 'Remove group' : 'Remove'}
                                                                </Button>
                                                            </div>
                                                        </IndexTable.Cell>
                                                    </IndexTable.Row>
                                                );
                                            })}
                                        </IndexTable>
                                    ) : !loading ? (
                                        <div style={{ padding: 16, border: '1px solid #e3e3e3', borderRadius: 8, background: '#fff' }}>
                                            <Text as="p" variant="bodyMd" tone="subdued">
                                                {searchTerm ? 'No mapped vehicles match this search.' : 'No vehicles are mapped to this collection.'}
                                            </Text>
                                        </div>
                                    ) : null}

                                    {(details.hasPrevious || details.hasNext) && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                                            <Text as="p" variant="bodySm" tone="subdued">Page {details.page}</Text>
                                            <Pagination
                                                hasPrevious={details.hasPrevious}
                                                hasNext={details.hasNext}
                                                onPrevious={() => setPage((current) => Math.max(1, current - 1))}
                                                onNext={() => setPage((current) => current + 1)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </div>
                ) : null}
            </Page>
        </>
    );
};

export default CollectionDetailsPage;
